import Script from 'next/script'
import { createContext, PropsWithChildren, useContext } from 'react'

import { listModelAccessForUserRole, PermissionDetail } from '@common/util/permissions'
import { useAdminContext } from '~/components/hooks/useAdminContext'
import { useCurrentUser } from '~/components/hooks/useCurrentUser'
import { Feature } from '~/graphql/generated/query.types'
import { isProd } from '~/util/env'
import { avatarSidToUrl } from '~/util/s3'
import { useFeatureFlag } from '~/util/useFeatureFlag'

declare global {
  interface Window {
    FrigadeAssistant: {
      (config: {
        apiKey: string
        apiUrl: string
        assistantId: string
        userId: string
        userProperties: {
          firstName: string
          lastName: string
          imageUrl?: string
          email: string
          role: string
          permissions: PermissionDetail[]
        }
        showWidget?: boolean
      }): void
      q?: unknown[]
      on?: {
        (event: string, handler: unknown): void
        q?: { event: string; handler: unknown }[]
      }
      show: () => void
      hide: () => void
      getCurrentState: () => 'hidden' | 'widget'
    }
  }
}

interface FrigadeAssistantContextValue {
  show: () => void
  hide: () => void
  isEnabled: boolean
}

const FrigadeAssistantContext = createContext<FrigadeAssistantContextValue>({
  show: () => {},
  hide: () => {},
  isEnabled: false,
})

export function useFrigadeAssistant() {
  return useContext(FrigadeAssistantContext)
}

export function FrigadeAssistantWrapper({ children }: PropsWithChildren) {
  const user = useCurrentUser()
  const { isImpersonating } = useAdminContext()
  const isFrigadeAssistantEnabled = useFeatureFlag(Feature.frigadeAssistant)
  const frigadeApiKey = process.env.NEXT_PUBLIC_FRIGADE_API_KEY
  const frigadeAssistantId = process.env.NEXT_PUBLIC_FRIGADE_ASSISTANT_ID
  const isEnabled = isProd && isFrigadeAssistantEnabled && !!frigadeApiKey && !!frigadeAssistantId
  // const theme = useTheme()
  // const { colorMode } = useColorMode()

  const contextValue: FrigadeAssistantContextValue = isEnabled
    ? {
        show: () => {
          if (window.FrigadeAssistant?.show) {
            window.FrigadeAssistant.show()
          }
        },
        hide: () => {
          if (window.FrigadeAssistant?.hide) {
            window.FrigadeAssistant.hide()
          }
        },
        isEnabled,
      }
    : {
        show: () => {},
        hide: () => {},
        isEnabled,
      }

  // TODO(scott, 2025-10-16): Get the widget to look nice with our theme values
  // const frigadeCssVariables = useMemo(() => {
  //   if (!isEnabled) return ''

  //   const { colors } = theme
  //   const brand = colors.brand ?? colors.purple
  //   const gray = colors.gray
  //   const blue = colors.blue
  //   const green = colors.green
  //   const red = colors.red
  //   const yellow = colors.yellow
  //   const isDark = colorMode === 'dark'

  //   return `
  //     #frigade-root {
  //       /* Blue scale */
  //       --fr-colors-blue-100: ${blue[100]};
  //       --fr-colors-blue-200: ${blue[200]};
  //       --fr-colors-blue-300: ${blue[300]};
  //       --fr-colors-blue-400: ${blue[400]};
  //       --fr-colors-blue-500: ${blue[500]};
  //       --fr-colors-blue-600: ${blue[600]};
  //       --fr-colors-blue-700: ${blue[700]};
  //       --fr-colors-blue-800: ${blue[800]};
  //       --fr-colors-blue-900: ${blue[900]};

  //       /* Gray scale */
  //       --fr-colors-gray-100: ${gray[100]};
  //       --fr-colors-gray-200: ${gray[200]};
  //       --fr-colors-gray-300: ${gray[300]};
  //       --fr-colors-gray-400: ${gray[400]};
  //       --fr-colors-gray-500: ${gray[500]};
  //       --fr-colors-gray-600: ${gray[600]};
  //       --fr-colors-gray-700: ${gray[700]};
  //       --fr-colors-gray-800: ${gray[800]};
  //       --fr-colors-gray-900: ${gray[900]};

  //       /* Green scale */
  //       --fr-colors-green-100: ${green[100]};
  //       --fr-colors-green-200: ${green[200]};
  //       --fr-colors-green-300: ${green[300]};
  //       --fr-colors-green-400: ${green[400]};
  //       --fr-colors-green-500: ${green[500]};
  //       --fr-colors-green-600: ${green[600]};
  //       --fr-colors-green-700: ${green[700]};
  //       --fr-colors-green-800: ${green[800]};
  //       --fr-colors-green-900: ${green[900]};

  //       /* Red scale */
  //       --fr-colors-red-100: ${red[100]};
  //       --fr-colors-red-200: ${red[200]};
  //       --fr-colors-red-300: ${red[300]};
  //       --fr-colors-red-400: ${red[400]};
  //       --fr-colors-red-500: ${red[500]};
  //       --fr-colors-red-600: ${red[600]};
  //       --fr-colors-red-700: ${red[700]};
  //       --fr-colors-red-800: ${red[800]};
  //       --fr-colors-red-900: ${red[900]};

  //       /* Yellow scale */
  //       --fr-colors-yellow-100: ${yellow[100]};
  //       --fr-colors-yellow-200: ${yellow[200]};
  //       --fr-colors-yellow-300: ${yellow[300]};
  //       --fr-colors-yellow-400: ${yellow[400]};
  //       --fr-colors-yellow-500: ${yellow[500]};
  //       --fr-colors-yellow-600: ${yellow[600]};
  //       --fr-colors-yellow-700: ${yellow[700]};
  //       --fr-colors-yellow-800: ${yellow[800]};
  //       --fr-colors-yellow-900: ${yellow[900]};

  //       /* Neutral semantic colors - color mode aware */
  //       --fr-colors-neutral-100: ${gray[100]};
  //       --fr-colors-neutral-200: ${gray[200]};
  //       --fr-colors-neutral-300: ${gray[300]};
  //       --fr-colors-neutral-400: ${gray[400]};
  //       --fr-colors-neutral-500: ${gray[500]};
  //       --fr-colors-neutral-600: ${gray[600]};
  //       --fr-colors-neutral-700: ${gray[700]};
  //       --fr-colors-neutral-800: ${gray[800]};
  //       --fr-colors-neutral-900: ${gray[900]};
  //       --fr-colors-neutral-background: ${isDark ? gray[800] : colors.white};
  //       --fr-colors-neutral-border: ${isDark ? gray[700] : gray[100]};
  //       --fr-colors-neutral-foreground: ${isDark ? gray[100] : gray[900]};
  //       --fr-colors-neutral-surface: ${isDark ? gray[800] : gray[100]};
  //       --fr-colors-neutral-active-background: ${isDark ? gray[800] : colors.white};
  //       --fr-colors-neutral-active-border: ${isDark ? gray[700] : gray[100]};
  //       --fr-colors-neutral-active-foreground: ${isDark ? gray[100] : gray[900]};
  //       --fr-colors-neutral-active-surface: ${isDark ? gray[800] : gray[100]};
  //       --fr-colors-neutral-focus-background: ${isDark ? gray[800] : colors.white};
  //       --fr-colors-neutral-focus-border: ${isDark ? gray[700] : gray[100]};
  //       --fr-colors-neutral-focus-foreground: ${isDark ? gray[100] : gray[900]};
  //       --fr-colors-neutral-focus-surface: ${isDark ? gray[800] : gray[100]};
  //       --fr-colors-neutral-hover-background: ${isDark ? gray[800] : colors.white};
  //       --fr-colors-neutral-hover-border: ${isDark ? gray[700] : gray[100]};
  //       --fr-colors-neutral-hover-foreground: ${isDark ? gray[100] : gray[900]};
  //       --fr-colors-neutral-hover-surface: ${isDark ? gray[800] : gray[100]};

  //       /* Primary (brand) semantic colors - color mode aware */
  //       --fr-colors-primary-100: ${brand[50]};
  //       --fr-colors-primary-200: ${brand[100]};
  //       --fr-colors-primary-300: ${brand[200]};
  //       --fr-colors-primary-400: ${brand[300]};
  //       --fr-colors-primary-500: ${brand[400]};
  //       --fr-colors-primary-600: ${brand[500]};
  //       --fr-colors-primary-700: ${brand[600]};
  //       --fr-colors-primary-800: ${brand[700]};
  //       --fr-colors-primary-900: ${brand[800]};
  //       --fr-colors-primary-background: ${isDark ? brand[400] : brand[500]};
  //       --fr-colors-primary-border: ${isDark ? brand[400] : brand[500]};
  //       --fr-colors-primary-foreground: ${colors.white};
  //       --fr-colors-primary-surface: ${isDark ? brand[400] : brand[500]};
  //       --fr-colors-primary-active-background: ${isDark ? brand[500] : brand[600]};
  //       --fr-colors-primary-active-border: ${isDark ? brand[500] : brand[600]};
  //       --fr-colors-primary-active-foreground: ${colors.white};
  //       --fr-colors-primary-active-surface: ${isDark ? brand[500] : brand[600]};
  //       --fr-colors-primary-focus-background: ${isDark ? brand[400] : brand[500]};
  //       --fr-colors-primary-focus-border: ${isDark ? brand[400] : brand[500]};
  //       --fr-colors-primary-focus-foreground: ${colors.white};
  //       --fr-colors-primary-focus-surface: ${isDark ? brand[400] : brand[500]};
  //       --fr-colors-primary-hover-background: ${isDark ? brand[300] : brand[400]};
  //       --fr-colors-primary-hover-border: ${isDark ? brand[300] : brand[400]};
  //       --fr-colors-primary-hover-foreground: ${colors.white};
  //       --fr-colors-primary-hover-surface: ${isDark ? brand[300] : brand[400]};

  //       /* Secondary semantic colors - color mode aware */
  //       --fr-colors-secondary-100: ${isDark ? gray[900] : gray[100]};
  //       --fr-colors-secondary-200: ${isDark ? gray[800] : gray[200]};
  //       --fr-colors-secondary-300: ${isDark ? gray[700] : gray[300]};
  //       --fr-colors-secondary-400: ${isDark ? gray[600] : gray[400]};
  //       --fr-colors-secondary-500: ${isDark ? gray[500] : gray[500]};
  //       --fr-colors-secondary-600: ${isDark ? gray[400] : gray[600]};
  //       --fr-colors-secondary-700: ${isDark ? gray[300] : gray[700]};
  //       --fr-colors-secondary-800: ${isDark ? gray[200] : gray[800]};
  //       --fr-colors-secondary-900: ${isDark ? gray[100] : gray[900]};
  //       --fr-colors-secondary-background: ${isDark ? gray[900] : colors.white};
  //       --fr-colors-secondary-border: ${isDark ? gray[700] : gray[300]};
  //       --fr-colors-secondary-foreground: ${isDark ? gray[100] : gray[800]};
  //       --fr-colors-secondary-surface: ${isDark ? gray[800] : gray[100]};
  //       --fr-colors-secondary-active-background: ${isDark ? gray[800] : gray[200]};
  //       --fr-colors-secondary-active-border: ${isDark ? gray[600] : gray[400]};
  //       --fr-colors-secondary-active-foreground: ${isDark ? gray[50] : gray[900]};
  //       --fr-colors-secondary-active-surface: ${isDark ? gray[700] : gray[200]};
  //       --fr-colors-secondary-focus-background: ${isDark ? gray[900] : colors.white};
  //       --fr-colors-secondary-focus-border: ${isDark ? gray[600] : gray[400]};
  //       --fr-colors-secondary-focus-foreground: ${isDark ? gray[50] : gray[900]};
  //       --fr-colors-secondary-focus-surface: ${isDark ? gray[800] : gray[100]};
  //       --fr-colors-secondary-hover-background: ${isDark ? gray[800] : gray[50]};
  //       --fr-colors-secondary-hover-border: ${isDark ? gray[600] : gray[400]};
  //       --fr-colors-secondary-hover-foreground: ${isDark ? gray[50] : gray[900]};
  //       --fr-colors-secondary-hover-surface: ${isDark ? gray[700] : gray[200]};
  //     }
  //   `
  // }, [isEnabled, theme, colorMode])

  if (!isEnabled) {
    return (
      <FrigadeAssistantContext.Provider value={contextValue}>
        {children}
      </FrigadeAssistantContext.Provider>
    )
  }

  // Split name into first and last
  const [firstName, ...lastNameParts] = user.fullName.split(' ')
  const lastName = lastNameParts.join(' ')
  const imageUrl = user.avatarSid ? avatarSidToUrl(user.avatarSid) : undefined
  const permissions = listModelAccessForUserRole(user.role)
  const userProperties = {
    firstName: `${isImpersonating ? '(impersonated) ' : ''}${firstName ?? user.fullName}`,
    lastName: lastName ?? '',
    imageUrl,
    email: user.email,
    role: user.role,
    permissions,
  }

  return (
    <FrigadeAssistantContext.Provider value={contextValue}>
      {/* {frigadeCssVariables && <style dangerouslySetInnerHTML={{ __html: frigadeCssVariables }} />} */}
      <Script
        id="frigade-assistant-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.FrigadeAssistant =
              window.FrigadeAssistant ||
              function (config) {
                (window.FrigadeAssistant.q = window.FrigadeAssistant.q || []).push(config);
              };
            window.FrigadeAssistant.on =
              window.FrigadeAssistant.on ||
              function (event, handler) {
                (window.FrigadeAssistant.on.q = window.FrigadeAssistant.on.q || []).push({
                  event,
                  handler,
                });
              };
          `,
        }}
      />
      <Script
        id="frigade-assistant-script"
        strategy="afterInteractive"
        src="https://app.frigade.com/cdn/frigade-assistant.min.js"
        onLoad={() => {
          window.FrigadeAssistant?.({
            apiKey: frigadeApiKey,
            apiUrl: 'https://api.frigade.ai',
            assistantId: frigadeAssistantId,
            userId: user.sid,
            userProperties,
            showWidget: false,
          })
        }}
      />
      {children}
    </FrigadeAssistantContext.Provider>
  )
}
